import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ChevronLeft,
  FolderOpen,
  X,
  Lock,
  Unlock,
  Crosshair,
  RotateCcw,
  AlignStartVertical,
  Zap,
  Activity,
  Film,
  Save,
  BarChart3,
} from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { showToast } from "utils/toast";
import {
  parseExportFile,
  buildExportFile,
  exportFileName,
} from "utils/exportFile";
import { saveTextFile } from "utils/fileDialog";
import { loadReview, saveReview, mergeReview } from "utils/videoReview";
import VideoReview from "./common/VideoReview";
import {
  buildExportResult,
  DEFAULT_IMPACT_THRESHOLD_G,
} from "utils/wearableExport";
import { wearableShortName } from "utils/centrifugeFile";
import {
  buildTrack,
  valueAt,
  segmentEnvelope,
  tracksExtent,
  alignStartOffsets,
  TRACK_COLORS,
  MAX_COMPARE_TRACKS,
} from "utils/compareTracks";
import { severityForG } from "./common/impactVisuals";
import LoadSeriesChart from "./common/LoadSeriesChart";
import { dateLocale } from "i18n";

const LANE_MIN = 88; // floor: ten tracks still readable (scrolls if needed)
const LANE_MAX = 320; // ceiling: one lone track shouldn't become a wall
const AXIS_H = 26;
const MIN_SPAN_MS = 20; // zoom floor: ~20 ms across the canvas

// Only the scalars the review reads. Dropping lowG/highG matters: those are
// the sample arrays, and the track already holds its own copy of them.
const slimForReview = (r) => ({
  sessionId: r.sessionId,
  impactId: r.impactId,
  frameIndex: r.frameIndex,
  timestampMs: r.timestampMs,
  timestampIsEpoch: r.timestampIsEpoch,
  peakMagG: r.peakMagG,
  durationMs: r.durationMs,
  overCount: r.overCount,
  band: r.band,
  isSnippet: r.isSnippet,
  sizeLabel: r.sizeLabel,
  gateFlagsLabel: r.gateFlagsLabel,
  gateFlags: r.gateFlags,
});

const hexA = (hex, a) =>
  `rgba(${parseInt(hex.slice(1, 3), 16)},${parseInt(
    hex.slice(3, 5),
    16
  )},${parseInt(hex.slice(5, 7), 16)},${a})`;

const gLabel = (v) => `${v.toFixed(v < 10 ? 1 : 0)}g`;

const fmtT = (ms, spanMs) => {
  if (ms == null) return "—";
  if (ms > 1e12) {
    const d = new Date(ms);
    const base = d.toLocaleTimeString(dateLocale("en-GB"), { hour12: false });
    return spanMs < 5000
      ? `${base}.${String(d.getMilliseconds()).padStart(3, "0")}`
      : base;
  }
  const s = ms / 1000;
  const mm = Math.floor(s / 60);
  const rest = (s - mm * 60).toFixed(spanMs < 5000 ? 3 : 1);
  return `${mm}:${String(rest).padStart(spanMs < 5000 ? 6 : 4, "0")}`;
};

const niceStep = (spanMs) => {
  const steps = [
    50, 100, 250, 500, 1000, 2000, 5000, 10000, 30000, 60000, 120000, 300000,
    600000, 1800000, 3600000,
  ];
  const target = spanMs / 8;
  return steps.find((s) => s >= target) || steps[steps.length - 1];
};

/**
 * Compare up to ten saved exports on one time axis: each file is a track
 * (waveform envelope + impact markers), aligned by the records' epoch
 * timestamps with a per-track nudge for unsynced clocks. Wheel zooms around
 * the mouse, drag pans, hover sweeps a cursor across every lane, click locks
 * it, and the inspector reads each track's exact sample (and nearest impact)
 * at the locked instant. Canvas-rendered — ten full stores is millions of
 * samples, far beyond a chart library.
 */
const CompareExportsView = ({ onExit, seed = null }) => {
  const { t } = useTranslation();
  const [tracks, setTracks] = useState([]);
  const [band, setBand] = useState("high");
  const [domain, setDomain] = useState(null); // {t0,t1} in absolute ms
  const [cursorT, setCursorT] = useState(null);
  const [locked, setLocked] = useState(false);
  const [show, setShow] = useState({ impacts: true, waveforms: true });
  // Review mode swaps the waveform board for the footage. Same files, same
  // shared clock -- a session is filmed once and worn by several athletes, so
  // the video is the thing they have in common.
  const [reviewMode, setReviewMode] = useState(false);
  // "impacts": lanes of waveforms + impact markers (and video review).
  // "load": the Σg-per-window chart, one series per file.
  const [screen, setScreen] = useState("impacts");
  const [selRec, setSelRec] = useState(null);
  const [savingId, setSavingId] = useState(null);
  // Live-measured canvas area — lanes stretch to fill the height, so one or
  // two tracks get tall readable lanes instead of thin strips atop a void.
  const [size, setSize] = useState({ w: 600, h: 400 });

  const fileRef = useRef(null);
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const dragRef = useRef(null);

  const extent = useMemo(() => tracksExtent(tracks), [tracks]);
  // Kind filter: strip impact or waveform records before drawing and
  // readouts. Extent stays on the full set so toggling never re-zooms.
  const viewTracks = useMemo(() => {
    if (show.impacts && show.waveforms) return tracks;
    const keep = (s) => (s.isSnippet ? show.waveforms : show.impacts);
    return tracks.map((tr) => ({
      ...tr,
      data: {
        ...tr.data,
        segments: {
          low: tr.data.segments.low.filter(keep),
          high: tr.data.segments.high.filter(keep),
        },
        impacts: tr.data.impacts.filter(keep),
      },
    }));
  }, [tracks, show]);
  const dom = domain || extent;
  const spanMs = dom ? dom.t1 - dom.t0 : 1;
  const laneH = tracks.length
    ? Math.max(
        LANE_MIN,
        Math.min(LANE_MAX, Math.floor((size.h - AXIS_H) / tracks.length))
      )
    : LANE_MIN;

  // ── Load files ──────────────────────────────────────────────────────
  const addFiles = useCallback(
    async (fileList) => {
      // A picked File or a {name, text} seed carried in from the single-file
      // view -- both only have to answer "what are you called" and "what do
      // you say".
      const files = Array.from(fileList || []).map((f) =>
        typeof f.text === "function"
          ? f
          : { name: f.name, text: async () => f.text }
      );
      const room = MAX_COMPARE_TRACKS - tracks.length;
      if (files.length > room) {
        showToast.error(
          room > 0
            ? t("analysis.compareExports.tooManyRoom", {
                max: MAX_COMPARE_TRACKS,
                room,
              })
            : t("analysis.compareExports.tooManyFull", {
                max: MAX_COMPARE_TRACKS,
              })
        );
      }
      const next = [];
      for (const f of files.slice(0, Math.max(0, room))) {
        try {
          const text = await f.text();
          const parsedFile = parseExportFile(text);
          const decoded = buildExportResult(parsedFile.raw, {
            impactThresholdG: DEFAULT_IMPACT_THRESHOLD_G,
          });
          const data = buildTrack(decoded.records);
          if (!data.extent)
            throw new Error(t("analysis.compareExports.noDrawableRecords"));
          // Load windows aren't waveforms, so buildTrack ignores them -- pull
          // them off the decoded records for the load overlay chart. Flattened
          // across all LOAD_BINS records and sorted by elapsed-since-session.
          const loadWindows = decoded.records
            .filter((r) => r && r.isLoad)
            .flatMap((r) => r.loadWindows || [])
            .sort((a, b) => a.startOffsetMs - b.startOffsetMs);
          const mac = parsedFile.wearable?.ble_mac || f.name;
          const review = mergeReview(parsedFile.review, loadReview(mac));
          next.push({
            id: `${Date.now()}-${Math.random()}`,
            fileName: f.name,
            name: wearableShortName(
              parsedFile.wearable,
              parsedFile.wearable?.ble_mac
            ),
            exportedAt: parsedFile.exportedAt,
            data,
            loadWindows,
            // ── Review + save-back ──
            // The File handle rather than the raw bytes: a full store is tens
            // of megabytes of base64 per unit, and five of those resident is
            // an easy way to run a laptop out of memory. Saving re-reads from
            // disk, which is a millisecond and always current.
            file: f,
            mac,
            meta: {
              wearable: parsedFile.wearable,
              status: parsedFile.status,
              clock: parsedFile.clock,
            },
            // Sample arrays are already copied into the track's Float32Arrays;
            // the review only needs each event's scalars, so the decoded
            // records are dropped rather than held twice.
            records: decoded.records
              .filter((r) => r && !r.isMarker && r.isValid !== false)
              .map(slimForReview),
            // A file may already carry verdicts (export v3+). Anything decided
            // on this machine still wins -- see mergeReview. (Bound above so
            // the alignment can be read out of it.)
            review,
            // Restored from the file (or this browser), so a multi-unit review
            // reopens aligned rather than needing every device lined up again.
            offsetMs: review.offsetMs || 0,
          });
        } catch (err) {
          showToast.error(
            t("analysis.compareExports.fileError", {
              name: f.name,
              message: err.message,
            })
          );
        }
      }
      if (next.length) {
        setTracks((prev) => [...prev, ...next]);
        setDomain(null); // refit to the new combined extent
      }
    },
    [tracks.length, t]
  );

  // ── Review ──────────────────────────────────────────────────────────
  // Each track keeps its OWN review object. That is what lets every file be
  // saved back carrying its own tagging, and it is why unit A's "impact 3"
  // cannot be confused with unit B's.
  const updateTrackReview = useCallback((id, fn) => {
    setTracks((prev) =>
      prev.map((t) => {
        if (t.id !== id) return t;
        const review = typeof fn === "function" ? fn(t.review) : fn;
        saveReview(t.mac, review);
        return { ...t, review };
      })
    );
  }, []);

  const setTrackOffset = useCallback((id, ms) => {
    setTracks((prev) =>
      prev.map((t) => {
        if (t.id !== id) return t;
        const review = { ...t.review, offsetMs: ms };
        saveReview(t.mac, review);
        return { ...t, offsetMs: ms, review };
      })
    );
  }, []);

  /** Write one file back out with its verdicts in it. The raw bytes are re-read
   *  from disk rather than held in memory, so the saved copy is byte-identical
   *  to the original export with only the review and digest rebuilt. Defined
   *  before `sources` because that memo lists it in its deps. */
  const saveTrack = useCallback(
    async (track) => {
      setSavingId(track.id);
      try {
        const file = parseExportFile(await track.file.text());
        const decoded = buildExportResult(file.raw, {
          impactThresholdG: DEFAULT_IMPACT_THRESHOLD_G,
        });
        const { json } = buildExportFile({
          wearable: file.wearable,
          raw: file.raw,
          // The export happened when it happened; saving a review later must not
          // rewrite when the data came off the device.
          exportedAt: file.exportedAt,
          status: file.status,
          clock: file.clock,
          parsed: decoded,
          review: track.review,
        });
        const res = await saveTextFile({
          defaultName:
            track.fileName ||
            exportFileName({ wearable: file.wearable, mac: track.mac }),
          contents: json,
        });
        if (!res?.canceled)
          showToast.success(
            t("analysis.compareExports.saved", { name: track.name })
          );
      } catch (err) {
        showToast.error(
          t("analysis.compareExports.fileError", {
            name: track.name,
            message: err.message,
          })
        );
      } finally {
        setSavingId(null);
      }
    },
    [t]
  );

  const sources = useMemo(
    () =>
      tracks.map((t, i) => ({
        id: t.id,
        name: t.name,
        color: TRACK_COLORS[i % TRACK_COLORS.length],
        records: t.records,
        review: t.review,
        offsetMs: t.offsetMs,
        onReview: (fn) => updateTrackReview(t.id, fn),
        // The canvas nudge and the review's alignment are one number, not two:
        // a unit lined up against the video is lined up against the other
        // tracks. Persisted with the review so it reopens aligned.
        onOffset: (ms) => setTrackOffset(t.id, ms),
        fileName: t.fileName,
        onSave: () => saveTrack(t),
        saving: savingId === t.id,
        onRemove: () => setTracks((prev) => prev.filter((x) => x.id !== t.id)),
      })),
    [tracks, updateTrackReview, setTrackOffset, saveTrack, savingId]
  );

  // The file the single-file view handed over. Loaded once, by name, so a
  // re-render cannot double it up.
  const seededRef = useRef(null);
  useEffect(() => {
    if (!seed?.text || seededRef.current === seed.name) return;
    seededRef.current = seed.name;
    addFiles([seed]);
  }, [seed, addFiles]);

  // ── Coordinate mapping ──────────────────────────────────────────────
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => {
      setSize({ w: el.clientWidth, h: el.clientHeight });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [tracks.length === 0]);

  const tToX = useCallback(
    (t) => ((t - dom.t0) / spanMs) * size.w,
    [dom, spanMs, size.w]
  );
  const xToT = useCallback(
    (x) => dom.t0 + (x / size.w) * spanMs,
    [dom, spanMs, size.w]
  );

  // ── Canvas drawing ──────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !dom || tracks.length === 0) return;
    const w = size.w;
    const h = Math.max(size.h, tracks.length * laneH + AXIS_H);
    const dpr = window.devicePixelRatio || 1;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    // Ground + vertical time grid painted first, waveforms over it.
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, w, h);
    const axisY = viewTracks.length * laneH;
    const step = niceStep(spanMs);
    const first = Math.ceil(dom.t0 / step) * step;
    ctx.strokeStyle = "#F4F1EE";
    ctx.beginPath();
    for (let t = first; t <= dom.t1; t += step) {
      const gx = Math.round(tToX(t)) + 0.5;
      ctx.moveTo(gx, 0);
      ctx.lineTo(gx, axisY);
    }
    ctx.stroke();

    viewTracks.forEach((tr, ti) => {
      const y0 = ti * laneH;
      const color = TRACK_COLORS[ti % TRACK_COLORS.length];
      ctx.strokeStyle = "#EDEBE7";
      ctx.beginPath();
      ctx.moveTo(0, y0 + laneH - 0.5);
      ctx.lineTo(w, y0 + laneH - 0.5);
      ctx.stroke();

      const segs = tr.data.segments[band];
      const off = tr.offsetMs || 0;
      const pxSpan = spanMs / w;

      // Pass 1: peak per pixel column + visible max for the lane scale.
      const cols = new Array(w);
      let visMax = 1;
      let si = 0;
      for (let x = 0; x < w; x++) {
        const t0 = dom.t0 + x * pxSpan - off;
        const t1 = t0 + pxSpan;
        while (si < segs.length && segs[si].startMs + segs[si].durMs < t0) si++;
        let mx = -Infinity;
        for (let k = si; k < segs.length && segs[k].startMs < t1; k++) {
          const env = segmentEnvelope(segs[k], t0, t1);
          if (env && env.max > mx) mx = env.max;
        }
        if (mx > -Infinity) {
          cols[x] = mx;
          if (mx > visMax) visMax = mx;
        }
      }
      const padTop = 18;
      const baseY = y0 + laneH - 8;
      const plotH = laneH - 8 - padTop;
      const yOf = (g) => baseY - (Math.max(0, g) / visMax) * plotH;

      // Dotted g-scale reference lines at ½ and full scale.
      ctx.save();
      ctx.strokeStyle = "#E9E6E1";
      ctx.setLineDash([2, 4]);
      ctx.beginPath();
      for (const frac of [0.5, 1]) {
        const gy = Math.round(yOf(visMax * frac)) + 0.5;
        ctx.moveTo(34, gy);
        ctx.lineTo(w, gy);
      }
      ctx.stroke();
      ctx.restore();

      // Pass 2: contiguous runs → soft gradient area under a crisp peak
      // line. Reads like a waveform, not the old per-pixel bar columns.
      const grad = ctx.createLinearGradient(0, y0 + padTop, 0, baseY);
      grad.addColorStop(0, hexA(color, 0.22));
      grad.addColorStop(1, hexA(color, 0.02));
      let run = [];
      const flush = () => {
        if (!run.length) return;
        if (run.length === 1) {
          ctx.fillStyle = hexA(color, 0.9);
          ctx.beginPath();
          ctx.arc(run[0][0], run[0][1], 1.2, 0, Math.PI * 2);
          ctx.fill();
          run = [];
          return;
        }
        ctx.beginPath();
        run.forEach(([px, py], i) =>
          i ? ctx.lineTo(px, py) : ctx.moveTo(px, py)
        );
        ctx.lineTo(run[run.length - 1][0], baseY);
        ctx.lineTo(run[0][0], baseY);
        ctx.closePath();
        ctx.fillStyle = grad;
        ctx.fill();
        ctx.beginPath();
        run.forEach(([px, py], i) =>
          i ? ctx.lineTo(px, py) : ctx.moveTo(px, py)
        );
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.4;
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        ctx.stroke();
        ctx.lineWidth = 1;
        run = [];
      };
      for (let x = 0; x < w; x++) {
        if (cols[x] == null) flush();
        else run.push([x + 0.5, yOf(cols[x])]);
      }
      flush();

      // Markers: faint hairline at the record's instant + a dot pinned to
      // its peak. Impacts filled, waveform snippets hollow.
      for (const imp of tr.data.impacts) {
        const t = imp.tMs + off;
        if (t < dom.t0 || t > dom.t1) continue;
        const x = tToX(t);
        const sev = severityForG(imp.peakG, false).color;
        const yPk = Math.max(y0 + padTop, yOf(imp.peakG));
        ctx.strokeStyle = hexA(sev, 0.25);
        ctx.beginPath();
        ctx.moveTo(Math.round(x) + 0.5, y0 + padTop - 6);
        ctx.lineTo(Math.round(x) + 0.5, baseY);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(x, yPk, 3, 0, Math.PI * 2);
        if (imp.isSnippet) {
          ctx.fillStyle = "#FFFFFF";
          ctx.fill();
          ctx.strokeStyle = sev;
          ctx.lineWidth = 1.5;
          ctx.stroke();
          ctx.lineWidth = 1;
        } else {
          ctx.fillStyle = sev;
          ctx.fill();
          ctx.strokeStyle = "#FFFFFF";
          ctx.stroke();
        }
      }

      // g labels sitting on the reference lines.
      ctx.fillStyle = "#C2BDB6";
      ctx.font = "9px ui-monospace, monospace";
      ctx.fillText(gLabel(visMax), 4, yOf(visMax) + 3);
      ctx.fillText(gLabel(visMax / 2), 4, yOf(visMax / 2) + 3);
    });

    // Time axis: hairline, tick marks, labels.
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, axisY, w, h - axisY);
    ctx.strokeStyle = "#E5E1DC";
    ctx.beginPath();
    ctx.moveTo(0, axisY + 0.5);
    ctx.lineTo(w, axisY + 0.5);
    ctx.stroke();
    ctx.fillStyle = "#A8A29E";
    ctx.font = "10px ui-monospace, monospace";
    ctx.beginPath();
    for (let t = first; t <= dom.t1; t += step) {
      const gx = Math.round(tToX(t)) + 0.5;
      ctx.moveTo(gx, axisY);
      ctx.lineTo(gx, axisY + 4);
      ctx.fillText(fmtT(t, spanMs), gx + 3.5, axisY + 16);
    }
    ctx.stroke();
    // Deps below: redraw on data/view changes only — NOT on cursor moves,
    // which are a DOM overlay. Envelope recompute per mousemove would jank
    // with ten full tracks loaded.
    // eslint-disable-next-line
  }, [viewTracks, band, dom, spanMs, tToX, size, laneH]);

  // ── Interactions ────────────────────────────────────────────────────
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!dom) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const frac = (e.clientX - rect.left) / rect.width;
      const factor = e.deltaY > 0 ? 1.25 : 0.8;
      const newSpan = Math.max(
        MIN_SPAN_MS,
        Math.min(spanMs * factor, extent ? extent.t1 - extent.t0 : spanMs)
      );
      const anchor = dom.t0 + frac * spanMs;
      let t0 = anchor - frac * newSpan;
      let t1 = t0 + newSpan;
      if (extent) {
        if (t0 < extent.t0) {
          t0 = extent.t0;
          t1 = t0 + newSpan;
        }
        if (t1 > extent.t1) {
          t1 = extent.t1;
          t0 = t1 - newSpan;
        }
      }
      setDomain({ t0, t1 });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [dom, spanMs, extent]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setLocked(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onMouseDown = (e) => {
    dragRef.current = {
      x: e.clientX,
      t0: dom?.t0,
      t1: dom?.t1,
      moved: false,
    };
  };
  const onMouseMove = (e) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const d = dragRef.current;
    if (d && e.buttons === 1 && dom) {
      const dx = e.clientX - d.x;
      if (Math.abs(dx) > 3) d.moved = true;
      if (d.moved) {
        const dt = (-dx / rect.width) * (d.t1 - d.t0);
        let t0 = d.t0 + dt;
        let t1 = d.t1 + dt;
        if (extent) {
          if (t0 < extent.t0) {
            t0 = extent.t0;
            t1 = t0 + (d.t1 - d.t0);
          }
          if (t1 > extent.t1) {
            t1 = extent.t1;
            t0 = t1 - (d.t1 - d.t0);
          }
        }
        setDomain({ t0, t1 });
        return;
      }
    }
    if (!locked && dom) setCursorT(xToT(x));
  };
  const onMouseUp = (e) => {
    const d = dragRef.current;
    dragRef.current = null;
    if (d && !d.moved && dom) {
      const rect = wrapRef.current.getBoundingClientRect();
      const t = xToT(e.clientX - rect.left);
      if (locked) {
        setCursorT(t); // move the lock
      } else {
        setCursorT(t);
        setLocked(true);
      }
    }
  };

  // ── Inspector data ──────────────────────────────────────────────────
  const readings = useMemo(() => {
    if (cursorT == null) return [];
    return viewTracks.map((tr, ti) => {
      const t = cursorT - (tr.offsetMs || 0);
      const v = valueAt(tr.data, band, t);
      // nearest impact
      let nearest = null;
      for (const imp of tr.data.impacts) {
        const d = Math.abs(imp.tMs - t);
        if (!nearest || d < nearest.d) nearest = { ...imp, d };
      }
      return { tr, ti, v, nearest };
    });
  }, [viewTracks, cursorT, band]);

  const setOffset = (id, ms) => setTrackOffset(id, ms);

  const anyNonEpoch = tracks.some((t) => !t.data.epochTimed);

  // One load series per file that carries LOAD_BINS windows.
  const loadSeries = useMemo(
    () =>
      tracks
        .map((tr, i) => ({
          id: tr.id,
          name: tr.name,
          color: TRACK_COLORS[i % TRACK_COLORS.length],
          windows: (tr.loadWindows || []).map((w) => ({
            offsetMs: w.startOffsetMs,
            sumG: w.sumG,
            peakG: w.peakG,
            counts: w.counts,
          })),
        }))
        .filter((s) => s.windows.length > 0),
    [tracks]
  );

  return (
    <div className="flex h-full min-h-0 flex-col px-6 py-5">
      {/* Header */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <button
          onClick={onExit}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-[#E5E7EB] bg-white text-[#6B7280] transition duration-150 hover:bg-[#F9FAFB] hover:text-[#374151]"
          title={t("common.actions.back")}
        >
          <ChevronLeft size={16} />
        </button>
        <div>
          <p className="text-sm font-semibold text-[#111827]">
            {t("analysis.compareExports.title")}
          </p>
          <p className="text-[11px] text-[#9CA3AF]">
            {t(
              screen === "load"
                ? "analysis.compareExports.subtitleLoad"
                : reviewMode
                ? "analysis.compareExports.subtitleReview"
                : "analysis.compareExports.subtitleImpacts",
              { n: tracks.length, max: MAX_COMPARE_TRACKS }
            )}
          </p>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {tracks.length > 0 && screen !== "load" && (
            <button
              onClick={() => setReviewMode((v) => !v)}
              className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold transition duration-150 ${
                reviewMode
                  ? "border-[#E07A3A] bg-[#FFF3ED] text-[#E07A3A]"
                  : "border-[#E5E7EB] bg-white text-[#374151] hover:bg-[#F9FAFB]"
              }`}
            >
              <Film size={12} />
              {reviewMode
                ? t("analysis.compareExports.waveforms")
                : t("analysis.compareExports.reviewVideo")}
            </button>
          )}
          {reviewMode && (
            <button
              onClick={() => tracks.forEach(saveTrack)}
              disabled={!!savingId}
              title={t("analysis.compareExports.saveAllHint")}
              className="flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-2.5 py-1.5 text-[11px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-40"
            >
              <Save size={12} />
              {t("analysis.compareExports.saveAll", { n: tracks.length })}
            </button>
          )}
          <div
            className={`items-center gap-1 rounded-lg border border-[#E5E7EB] bg-white p-0.5 ${
              reviewMode || screen === "load" ? "hidden" : "flex"
            }`}
          >
            <button
              onClick={() => setShow((s) => ({ ...s, impacts: !s.impacts }))}
              title={t("analysis.compareExports.toggleImpactsHint")}
              className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-semibold transition duration-150 ${
                show.impacts
                  ? "bg-[#FFF3ED] text-[#E07A3A]"
                  : "text-[#B8B3AC] hover:bg-[#F3F4F6]"
              }`}
            >
              <Zap size={11} />
              {t("analysis.compareExports.impacts")}
            </button>
            <button
              onClick={() =>
                setShow((s) => ({ ...s, waveforms: !s.waveforms }))
              }
              title={t("analysis.compareExports.toggleWaveformsHint")}
              className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-semibold transition duration-150 ${
                show.waveforms
                  ? "bg-[#EFF6FF] text-[#2563EB]"
                  : "text-[#B8B3AC] hover:bg-[#F3F4F6]"
              }`}
            >
              <Activity size={11} />
              {t("analysis.compareExports.waveforms")}
            </button>
          </div>
          <div
            className={`items-center gap-1 rounded-lg border border-[#E5E7EB] bg-white p-0.5 ${
              reviewMode || screen === "load" ? "hidden" : "flex"
            }`}
          >
            {["low", "high"].map((b) => (
              <button
                key={b}
                onClick={() => setBand(b)}
                className={`rounded-md px-2.5 py-1 text-[11px] font-semibold transition duration-150 ${
                  band === b
                    ? "bg-[#FFF3ED] text-[#E07A3A]"
                    : "text-[#6B7280] hover:bg-[#F3F4F6]"
                }`}
              >
                {b === "low"
                  ? t("analysis.compareExports.lowG")
                  : t("analysis.compareExports.highG")}
              </button>
            ))}
          </div>
          <button
            onClick={() =>
              setTracks((prev) => {
                const offs = alignStartOffsets(prev);
                return prev.map((tr, i) => ({ ...tr, offsetMs: offs[i] }));
              }) || setDomain(null)
            }
            disabled={tracks.length < 2}
            title={t("analysis.compareExports.alignStartsHint")}
            className={`items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-2.5 py-1.5 text-[11px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-40 ${
              reviewMode || screen === "load" ? "hidden" : "flex"
            }`}
          >
            <AlignStartVertical size={12} />
            {t("analysis.compareExports.alignStarts")}
          </button>
          <button
            onClick={() => setDomain(null)}
            disabled={!domain}
            className={`items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-2.5 py-1.5 text-[11px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-40 ${
              reviewMode || screen === "load" ? "hidden" : "flex"
            }`}
          >
            <RotateCcw size={12} />
            {t("analysis.compareExports.resetZoom")}
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={tracks.length >= MAX_COMPARE_TRACKS}
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-semibold text-white transition duration-150 disabled:opacity-50"
            style={{
              background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
            }}
          >
            <FolderOpen size={12} />
            {t("analysis.compareExports.addFiles")}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".json"
            multiple
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>
      </div>

      {/* Impacts / Load — one thing per screen */}
      {tracks.length > 0 && (
        <div className="mb-3 flex items-center gap-1 self-start rounded-lg bg-[#F3F4F6] p-0.5">
          {[
            {
              key: "impacts",
              label: t("analysis.compareExports.impacts"),
              Icon: Zap,
            },
            {
              key: "load",
              label: t("analysis.compareExports.load"),
              Icon: BarChart3,
            },
          ].map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setScreen(key)}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition duration-150 ${
                screen === key
                  ? "bg-white text-[#111827] shadow-sm"
                  : "text-[#6B7280] hover:text-[#374151]"
              }`}
            >
              <Icon size={12} />
              {label}
            </button>
          ))}
        </div>
      )}

      {anyNonEpoch && screen !== "load" && (
        <p className="mb-2 rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-1.5 text-[11px] text-[#92400E]">
          <Trans
            i18nKey="analysis.compareExports.nonEpochWarning"
            components={{ bold: <span className="font-semibold" /> }}
          />
        </p>
      )}

      {tracks.length > 0 && screen === "load" ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <LoadSeriesChart
            series={loadSeries}
            title={t("analysis.compareExports.load")}
            subtitle={t("analysis.compareExports.loadSubtitle")}
          />
        </div>
      ) : tracks.length > 0 && reviewMode ? (
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          <div className="min-h-0 flex-1">
            <VideoReview
              fill
              sources={sources}
              onAddUnits={(files) => addFiles(files)}
              selectedRecord={selRec}
              onSelectRecord={setSelRec}
              onScrubHandled={() => {}}
            />
          </div>
        </div>
      ) : tracks.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="rounded-xl border border-dashed border-[#E3DFD9] bg-white/60 px-10 py-12 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F5F4F1]">
              <Crosshair size={20} className="text-[#9CA3AF]" />
            </div>
            <p className="text-sm font-semibold text-[#111827]">
              {t("analysis.compareExports.emptyTitle", {
                max: MAX_COMPARE_TRACKS,
              })}
            </p>
            <p className="mx-auto mt-1 max-w-sm text-[13px] leading-relaxed text-[#6B7280]">
              {t("analysis.compareExports.emptyBody")}
            </p>
            <button
              onClick={() => fileRef.current?.click()}
              className="mt-5 inline-flex items-center gap-1.5 rounded-[10px] px-4 py-2.5 text-sm font-semibold text-white transition duration-150"
              style={{
                background: "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
              }}
            >
              <FolderOpen size={15} />
              {t("analysis.compareExports.addExportFiles")}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 gap-3">
          {/* Track gutter */}
          <div className="w-[180px] flex-shrink-0 overflow-y-auto">
            {tracks.map((tr, ti) => (
              <div
                key={tr.id}
                style={{ height: laneH }}
                className="flex flex-col justify-center gap-1 border-b border-[#EDEDEA] pr-2"
              >
                <div className="flex items-center gap-1.5">
                  <span
                    className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                    style={{
                      backgroundColor: TRACK_COLORS[ti % TRACK_COLORS.length],
                    }}
                  />
                  <span
                    className="min-w-0 flex-1 truncate text-[12px] font-semibold text-[#111827]"
                    title={tr.fileName}
                  >
                    {tr.name}
                  </span>
                  <button
                    onClick={() =>
                      setTracks((prev) => prev.filter((x) => x.id !== tr.id))
                    }
                    title={t("analysis.compareExports.removeTrack")}
                    className="flex h-5 w-5 items-center justify-center rounded text-[#B8B3AC] hover:bg-[#F3F4F6] hover:text-[#DC2626]"
                  >
                    <X size={11} />
                  </button>
                </div>
                <p className="truncate text-[10px] text-[#9CA3AF]">
                  {t("analysis.compareExports.trackSummary", {
                    impacts: tr.data.impactCount,
                    waves: tr.data.snippetCount,
                    peak: tr.data.peakG.toFixed(1),
                  })}
                </p>
                <label className="flex items-center gap-1 text-[10px] text-[#9CA3AF]">
                  {t("analysis.compareExports.offset")}
                  <input
                    type="number"
                    value={tr.offsetMs || 0}
                    onChange={(e) =>
                      setOffset(tr.id, Number(e.target.value) || 0)
                    }
                    className="w-16 rounded border border-[#E5E7EB] px-1 py-0.5 font-mono text-[10px] text-[#374151] outline-none focus:border-[#E07A3A]"
                  />
                  ms
                </label>
              </div>
            ))}
          </div>

          {/* Timeline canvas */}
          <div
            ref={wrapRef}
            className="relative min-w-0 flex-1 cursor-crosshair select-none overflow-hidden rounded-lg border border-[#EDEDEA]"
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onDoubleClick={() => setDomain(null)}
            onMouseLeave={() => {
              if (!locked) setCursorT(null);
              dragRef.current = null;
            }}
          >
            <canvas ref={canvasRef} />
            {cursorT != null &&
              dom &&
              cursorT >= dom.t0 &&
              cursorT <= dom.t1 && (
                <div
                  className="pointer-events-none absolute bottom-0 top-0"
                  style={{ left: tToX(cursorT) }}
                >
                  <div
                    className={`h-full w-px ${
                      locked ? "bg-[#111827]" : "bg-[#9CA3AF]"
                    }`}
                  />
                  <div
                    className={`absolute top-1 flex -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 font-mono text-[10px] text-white ${
                      locked ? "bg-[#111827]" : "bg-[#9CA3AF]"
                    }`}
                  >
                    {locked ? <Lock size={9} /> : null}
                    {fmtT(cursorT, spanMs)}
                  </div>
                </div>
              )}
          </div>

          {/* Inspector */}
          <div className="w-[220px] flex-shrink-0 overflow-y-auto">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#9CA3AF]">
                {t("analysis.compareExports.atCursor")}
              </span>
              {locked ? (
                <button
                  onClick={() => setLocked(false)}
                  className="flex items-center gap-1 rounded bg-[#111827] px-1.5 py-0.5 text-[10px] font-semibold text-white"
                  title={t("analysis.compareExports.unlockHint")}
                >
                  <Unlock size={9} />
                  {t("analysis.compareExports.locked")}
                </button>
              ) : (
                <span className="text-[10px] text-[#B8B3AC]">
                  {t("analysis.compareExports.clickToLock")}
                </span>
              )}
            </div>
            {cursorT == null ? (
              <p className="text-[12px] text-[#9CA3AF]">
                {t("analysis.compareExports.hoverHint")}
              </p>
            ) : (
              readings.map(({ tr, ti, v, nearest }) => (
                <div
                  key={tr.id}
                  className="mb-1.5 rounded-lg border border-[#F3F4F6] bg-white px-2.5 py-2"
                >
                  <div className="flex items-center gap-1.5">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{
                        backgroundColor: TRACK_COLORS[ti % TRACK_COLORS.length],
                      }}
                    />
                    <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-[#374151]">
                      {tr.name}
                    </span>
                    <span className="font-mono text-[13px] font-bold tabular-nums text-[#111827]">
                      {v ? `${v.mag.toFixed(2)}g` : "—"}
                    </span>
                  </div>
                  {v && (
                    <p className="mt-0.5 font-mono text-[10px] tabular-nums text-[#9CA3AF]">
                      x {v.x.toFixed(2)} · y {v.y.toFixed(2)} · z{" "}
                      {v.z.toFixed(2)}
                    </p>
                  )}
                  {nearest && nearest.d < 5000 && (
                    <p className="mt-0.5 text-[10px] text-[#9CA3AF]">
                      <Trans
                        i18nKey={
                          nearest.d < 1
                            ? "analysis.compareExports.nearestImpactHere"
                            : "analysis.compareExports.nearestImpactAway"
                        }
                        values={{
                          peak: nearest.peakG.toFixed(1),
                          seconds: (nearest.d / 1000).toFixed(2),
                        }}
                        components={{
                          peak: (
                            <span
                              className="font-mono font-semibold"
                              style={{
                                color: severityForG(nearest.peakG, false).color,
                              }}
                            />
                          ),
                        }}
                      />
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompareExportsView;
